import { defineStore } from "pinia";
import { createClient, SupabaseClient } from "@supabase/supabase-js";
import { useLogger } from "../composables/useLogger";
import { ref, computed, readonly } from 'vue'
import { useHeartbeatStore } from "../stores/heartbeatStore";

// Constants
const MAX_RETRIES = 16*30; // appr. 1 month
const BASE_DELAY = 1000; // 1 second
const MAX_DELAY = 1000*60*60*24; // 1 day

// Create a custom event for Supabase health state changes
const SUPABASE_HEALTH_EVENT = 'supabase-health-changed'

export const useSupabaseClientStore = defineStore("supabaseClient", () => {
  const logger = useLogger();
  let supabase: SupabaseClient | null = null;
  let retryCount = 0;
  
  // Reactive state for health and recovery
  const isHealthy = ref(true)
  const isRecovering = ref(false)
  let recoveryPromise: Promise<void> | null = null;

  // Watch network state from HeartbeatStore
  const heartbeatStore = useHeartbeatStore();

  /**
   * Handle network state changes from HeartbeatStore
   */
  function handleNetworkStateChange(isOnline: boolean) {
    if (!isOnline) {
      logger.debug('Supabase: Network went offline, pausing health checks');
      updateHealthState(false);
    }
  }

  initializeSupabase()

  /**
   * Update health state
   */
  function updateHealthState(healthy: boolean) {
    if (isHealthy.value !== healthy) {
      isHealthy.value = healthy;
    }
  }

  /**
   * Initializes the Supabase client
   * @throws {Error} If the client fails to initialize
   */
  function initializeSupabase() {
    try { 
      supabase = createClient(
        import.meta.env.VITE_SUPABASE_URL as string,
        import.meta.env.VITE_SUPABASE_KEY as string,
      );
    } catch (error) {
      supabase = null;
      logger.logErrorCaught('Failed to initialize Supabase client', 'initializeSupabase', error);
      updateHealthState(false);
      throw error;
    }
  }

  /**
   * Reinitializes the Supabase client
   * @throws {Error} If the client fails to reinitialize
   */
  async function reInitSupabase() {
    supabase = null;
    await initializeSupabase();
  }

  /**
   * Checks if the Supabase client is healthy by performing a simple query
   * @returns {Promise<boolean>} True if the client is currently healthy, false otherwise. False if the client is not initialized.
   */
  async function checkSupabaseHealth(): Promise<boolean> {
    if (!supabase) {
      updateHealthState(false);
      return false;
    }
    
    try {
      // Perform a simple health check query
      const { data, error } = await supabase.from('persons').select('count').limit(1).single();
      const healthy = !error;
      updateHealthState(healthy);
      
      if (!healthy) {
        logger.warn('Supabase health check failed', { error });
      }
      return healthy;
    } catch (error) {
      logger.warn('Supabase health check threw error', { error });
      updateHealthState(false);
      return false;
    }
  }

  /**
   * This checks if the supabase client is still connected to the database.
   * If not healthy, it will attempt to reinitialize with exponential backoff.
   * If a recovery is already in progress, returns the existing recovery promise.
   * Re-calls itself recursively until the client is healthy or the maximum number of retries is reached.
   * @returns {Promise<void>}
   */
  async function checkIfSupabaseProblem(): Promise<void> {
    // If already recovering, return existing promise
    if (isRecovering.value && recoveryPromise) {
      logger.debug('Supabase recovery already in progress, waiting for completion');
      return recoveryPromise;
    }

    // Start new recovery process
    isRecovering.value = true;
    recoveryPromise = (async () => {
      try {
        const healthy = await checkSupabaseHealth();

        if (!healthy) {
          logger.warn(`Supabase appears unhealthy, attempting recovery (attempt ${retryCount + 1}/${MAX_RETRIES})`);
          
          if (retryCount >= MAX_RETRIES) {
            logger.error('Supabase recovery failed after maximum retries');
            retryCount = 0;
            throw new Error('Failed to recover Supabase connection after maximum retries');
          }

          // Calculate delay with exponential backoff
          const delay = Math.min(BASE_DELAY * Math.pow(2, retryCount), MAX_DELAY);
          
          // Wait for the calculated delay
          await new Promise(resolve => setTimeout(resolve, delay));
          
          // Increment retry count and attempt reinitialization
          retryCount++;
          await reInitSupabase();
          
          // Recursively check health after reinitialization
          return checkIfSupabaseProblem();
        } else {
          // Reset retry count on successful health check
          retryCount = 0;
        }
      } catch (error) {
        logger.error('Supabase problem check failed', { error, retryCount });
        
        if (retryCount >= MAX_RETRIES) {
          retryCount = 0;
          throw error;
        }
        
        // Calculate delay with exponential backoff
        const delay = Math.min(BASE_DELAY * Math.pow(2, retryCount), MAX_DELAY);
        
        // Wait for the calculated delay
        await new Promise(resolve => setTimeout(resolve, delay));
        
        // Increment retry count and try again
        retryCount++;
        return checkIfSupabaseProblem();
      } finally {
        // Clear recovery state when done
        if (recoveryPromise === recoveryPromise) {
          isRecovering.value = false;
          recoveryPromise = null;
        }
      }
    })();

    return recoveryPromise;
  }

  /**
   * Force an immediate health check when conditions might have improved.
   * This bypasses any ongoing recovery process.
   * @returns {Promise<boolean>} True if healthy after check
   */
  async function checkHealthImmediately(): Promise<boolean> {
    // Cancel any ongoing recovery
    if (isRecovering.value) {
      logger.debug('Cancelling ongoing recovery for immediate health check');
      isRecovering.value = false;
      recoveryPromise = null;
      retryCount = 0;
    }

    // Perform immediate health check
    return checkSupabaseHealth();
  }

  // Initialize Supabase on store creation if we're on the client side
  if (process.client) {
    // Use IIFE to make initialization blocking
    (async () => {
      try {
        await initializeSupabase();
        await checkSupabaseHealth();
      } catch (error) {
        logger.logErrorCaught('Failed to initialize Supabase on store creation', 'supaBaseClientStore.ts', error);
        updateHealthState(false);
      }
    })();
  }

  return { 
    supabase, 
    checkIfSupabaseProblem,
    checkHealthImmediately,
    handleNetworkStateChange,
    isHealthy: readonly(isHealthy),
    isRecovering: readonly(isRecovering),
  };
});

// Export the event name for HeartbeatStore to use
export const SUPABASE_HEALTH_EVENT_NAME = SUPABASE_HEALTH_EVENT;
